import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Button,
  InputAdornment
} from '@mui/material';
import { 
  Help as HelpIcon, 
  CheckCircle as CheckIcon, 
  Error as ErrorIcon,
  AccountBalance as CouncilIcon // Icon for the Council
} from '@mui/icons-material';
import { useAIAssistant } from '../../contexts/AIAssistantContext';

// Interface matching your parent state
export interface Address {
  line1: string;
  line2?: string;
  city: string;
  postcode: string;
  country: string;
  // New fields for Planning Logic
  latitude?: number; 
  longitude?: number;
  council?: string;
  // Existing fields
  buildYear?: string;
  propertyType?: string;
  conservationArea?: string; // String 'yes' | 'no'
  conservationAreaName?: string;
}

interface AddressStepProps {
  initialData?: Address;
  data?: Address;
  onChange?: (address: Address) => void;
  onComplete?: (address: Address) => void;
  onError?: (error: string) => void;
}

const AddressStep: React.FC<AddressStepProps> = ({
  initialData,
  data,
  onChange,
  onComplete,
  onError
}) => {
  const { getExplanation } = useAIAssistant();
  
  // Local state for API lookup
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState<string>('');
  const [isPostcodeValid, setIsPostcodeValid] = useState<boolean | null>(null);
  const [helpText, setHelpText] = useState<string>('');

  // Merge props with defaults
  const currentData = data || initialData || {
    line1: '',
    line2: '',
    city: '',
    postcode: '',
    country: 'UK',
    buildYear: '',
    propertyType: '',
    conservationArea: 'no',
    conservationAreaName: ''
  };

  // --- API LOGIC ---
  const lookupPostcode = async () => {
    const cleanPostcode = currentData.postcode.replace(/\s/g, '');
    
    // 1. Basic Client-side check
    if (cleanPostcode.length < 5) {
      setLookupError('Postcode is too short');
      setIsPostcodeValid(false);
      return;
    }

    setIsLookingUp(true);
    setLookupError('');
    setIsPostcodeValid(null);

    try {
      // 2. Call Free API
      const response = await fetch(`https://api.postcodes.io/postcodes/${cleanPostcode}`);
      const result = await response.json();

      if (result.status === 200) {
        const r = result.result;
        
        // 3. Success: Auto-fill data
        const newData = {
          ...currentData,
          // We do NOT auto-fill City (it's often wrong in free APIs). User types it.
          city: currentData.city || '', 
          
          // We DO capture the Council (Critical for Planning)
          council: r.admin_district, 
          
          latitude: r.latitude,
          longitude: r.longitude,
          postcode: r.postcode // Formats it nicely (e.g. HA5 5HL)
        };

        setIsPostcodeValid(true);
        updateParent(newData);
      } else {
        // 4. API Error (Not found)
        setLookupError('Postcode not found. Please check and try again.');
        setIsPostcodeValid(false);
      }
    } catch (error) {
      setLookupError('Network error. Please enter address manually.');
      setIsPostcodeValid(false); // Allow manual entry on error
    } finally {
      setIsLookingUp(false);
    }
  };

  // Helper to update parent state
  const updateParent = (newData: Address) => {
    if (onChange) onChange(newData);
    if (onComplete) onComplete(newData);
  };

  const handleFieldChange = (field: keyof Address, value: any) => {
    // Reset validation status if user changes postcode
    if (field === 'postcode') {
      setIsPostcodeValid(null);
      setLookupError('');
    }

    const updatedAddress = { ...currentData, [field]: value };
    updateParent(updatedAddress);
  };

  const handleGetHelp = async (topic: string) => {
    try {
      const explanation = await getExplanation(topic);
      setHelpText(explanation);
    } catch (error) {
      console.error('Failed to get help:', error);
    }
  };

return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Property Address
        </Typography>
        <Tooltip title="Get help with address requirements">
          <IconButton
            size="small"
            onClick={() => handleGetHelp('UK property address requirements')}
            sx={{ ml: 1 }}
          >
            <HelpIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body1" color="text.secondary" paragraph>
        Enter your postcode to auto-fill location details. We use this to check for planning constraints.
      </Typography>

      {helpText && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setHelpText('')}>
          {helpText}
        </Alert>
      )}

      <Grid container spacing={3}>
        
        {/* 1. POSTCODE + FIND BUTTON (Left) */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              label="Postcode"
              placeholder="e.g. HA5 5HL"
              value={currentData.postcode}
              onChange={(e) => handleFieldChange('postcode', e.target.value.toUpperCase())}
              required
              error={!!lookupError}
              helperText={lookupError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isPostcodeValid === true && <CheckIcon color="success" />}
                    {isPostcodeValid === false && <ErrorIcon color="error" />}
                  </InputAdornment>
                )
              }}
            />
            <Button 
              variant="contained" 
              onClick={lookupPostcode}
              disabled={!currentData.postcode || isLookingUp}
              sx={{ minWidth: '100px' }}
            >
              {isLookingUp ? <CircularProgress size={24} color="inherit" /> : 'Find'}
            </Button>
          </Box>
        </Grid>

        {/* 2. PLANNING AUTHORITY (Right - Swapped Position) */}
        <Grid item xs={12} sm={6}>
          {currentData.council ? (
            <Alert 
              severity="info" 
              icon={<CouncilIcon fontSize="inherit" />}
              sx={{ 
                backgroundColor: '#e3f2fd', 
                color: '#0d47a1',
                height: '56px', // Matches TextField height for perfect alignment
                display: 'flex',
                alignItems: 'center',
                '& .MuiAlert-message': { width: '100%' } // Ensures text takes space
              }}
            >
              <Typography variant="body2" noWrap>
                <strong>Planning Authority:</strong> {currentData.council}
              </Typography>
            </Alert>
          ) : (
            // Optional: Placeholder to keep grid alignment if you prefer, 
            // or leave empty to let the next row float up (though that causes layout shift).
            // For a stable layout, we can leave this empty.
            <Box sx={{ height: '56px' }} /> 
          )}
        </Grid>

        {/* 3. ADDRESS LINE 1 (Full Width) */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 1"
            placeholder="e.g. 10 High Street"
            value={currentData.line1}
            onChange={(e) => handleFieldChange('line1', e.target.value)}
            required
            helperText="House number and street name"
          />
        </Grid>

        {/* 4. CITY (Moved Down) */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="City / Town"
            placeholder="e.g. Pinner"
            value={currentData.city}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            required
          />
        </Grid>

        {/* 5. ADDRESS LINE 2 (Moved Next to City) */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Address Line 2 (Optional)"
            placeholder="e.g. Flat 4B"
            value={currentData.line2 || ''}
            onChange={(e) => handleFieldChange('line2', e.target.value)}
          />
        </Grid>

        {/* 6. COUNTRY */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Country"
            value={currentData.country}
            disabled
            helperText="Currently supporting UK properties only"
          />
        </Grid>
      </Grid>

      {/* Property Details Section */}
      <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
        <Typography variant="h6" gutterBottom>
          Property Details
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              required
              label="Property Type"
              value={currentData.propertyType || ''}
              onChange={(e) => handleFieldChange('propertyType', e.target.value)}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="" disabled>Select Type...</option>
              <option value="detached">Detached House</option>
              <option value="semi-detached">Semi-Detached House</option>
              <option value="mid-terraced">Mid-Terraced House</option>
              <option value="end-terraced">End-Terraced House</option>
              <option value="bungalow">Bungalow</option>
              <option value="flat">Flat/Apartment</option>
              <option value="maisonette">Maisonette</option>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Approximate Build Year"
              placeholder="e.g. 1930s, 1980, Victorian"
              value={currentData.buildYear || ''}
              onChange={(e) => handleFieldChange('buildYear', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Is this in a Conservation Area?"
              value={currentData.conservationArea || 'no'}
              onChange={(e) => handleFieldChange('conservationArea', e.target.value)}
              SelectProps={{ native: true }}
              helperText="If yes, planning rules are stricter."
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </TextField>
          </Grid>

          {currentData.conservationArea === 'yes' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Conservation Area Name"
                placeholder="e.g. Hampstead Garden Suburb"
                value={currentData.conservationAreaName || ''}
                onChange={(e) => handleFieldChange('conservationAreaName', e.target.value)}
              />
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default AddressStep;